import { useState } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  useTheme,
  useMediaQuery,
  Container,
  Fade,
  Theme,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Logout,
  Settings,
  AdminPanelSettings,
  LightMode,
  DarkMode,
  ChevronLeft,
  ChevronRight,
} from '@mui/icons-material';
import { useCurrentUser } from '../hooks/useAuth';
import { useLogout } from '../hooks/useApi';
import { useThemeMode } from '../contexts/ThemeContext';
import { company } from '../config/branding';
import { FOAM_NAV } from '../config/navigation';
import ActiveRunSelector from '../components/layout/ActiveRunSelector';
import { en } from '../locale';

// Constants
const DRAWER_WIDTH = 280;
const AVATAR_GRADIENT = 'linear-gradient(135deg, #4a90e2 0%, #357abd 100%)';
const PRIMARY_COLOR = '#1e7ce2';

// Reusable styles
const getAvatarStyles = (size: number) => ({
  width: size,
  height: size,
  background: AVATAR_GRADIENT,
  fontWeight: 600,
  fontSize: `${size / 2.8}px`,
  border: '2px solid rgba(255,255,255,0.15)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: '#fff',
  textTransform: 'uppercase' as const,
});

const getMenuItemStyles = (theme: Theme, hoverColor: string = PRIMARY_COLOR) => ({
  py: 1.25,
  px: 2,
  mx: 1,
  my: 0.5,
  borderRadius: '8px',
  color: theme.palette.text.primary,
  transition: 'all 0.15s ease',
  '&:hover': {
    bgcolor: `rgba(${hoverColor === PRIMARY_COLOR ? '74,144,226' : '229,115,115'},0.08)`,
    color: hoverColor,
    '& .MuiListItemIcon-root': {
      color: hoverColor,
    },
  },
});

const FULLSCREEN_PATHS = new Set<string>(['/']);

interface UserAvatarProps {
  username?: string;
  size: number;
}

interface UserProfileProps {
  username?: string;
  roleDisplayName?: string;
  onProfileMenuOpen: (event: React.MouseEvent<HTMLElement>) => void;
}

interface NavigationListProps {
  isActive: (path: string) => boolean;
  onNavigate: (path: string) => void;
}

interface ProfileMenuProps {
  anchorEl: HTMLElement | null;
  username?: string;
  roleDisplayName?: string;
  onClose: () => void;
  onNavigate: (path: string) => void;
  onToggleTheme: () => void;
  onLogout: () => Promise<void>;
}

interface ToggleButtonProps {
  isOpen: boolean;
  onClick: () => void;
}

// Extracted Components
const UserAvatar = ({ username, size }: UserAvatarProps) => {
  const initial = username?.trim() ? username.trim().charAt(0).toUpperCase() : 'U';

  return <Avatar sx={getAvatarStyles(size)}>{initial}</Avatar>;
};

const UserProfile = ({ username, roleDisplayName, onProfileMenuOpen }: UserProfileProps) => {
  const theme = useTheme();
  const { mode } = useThemeMode();

  return (
    <Box
      sx={{
        p: 2,
        borderTop: `1px solid ${theme.palette.divider}`,
        background: mode === 'dark' ? 'rgba(0,0,0,0.15)' : 'rgba(0,0,0,0.02)',
        backdropFilter: 'blur(10px)',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1.5,
          p: 1.5,
          borderRadius: '12px',
          background: mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
          border: `1px solid ${theme.palette.divider}`,
          cursor: 'pointer',
          transition: 'all 0.2s ease',
          '&:hover': {
            background: mode === 'dark' ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
            transform: 'translateY(-1px)',
          },
        }}
        onClick={onProfileMenuOpen}
      >
        <UserAvatar username={username} size={42} />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              fontSize: '0.9rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {username ?? en.layout.userFallback}
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: theme.palette.text.secondary,
              fontSize: '0.75rem',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              display: 'block',
            }}
          >
            {roleDisplayName ?? 'User'}
          </Typography>
        </Box>
        <Settings sx={{ fontSize: '1rem', color: theme.palette.text.secondary }} />
      </Box>
    </Box>
  );
};

const DrawerHeader = () => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        p: 3,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        borderBottom: `1px solid ${theme.palette.divider}`,
        minHeight: '72px',
        position: 'relative',
        zIndex: 1,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box
          sx={{
            width: 40,
            height: 40,
            borderRadius: '10px',
            background: AVATAR_GRADIENT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: 700,
            fontSize: '1.2rem',
            color: '#fff',
            boxShadow: '0 2px 8px rgba(74,144,226,0.25)',
          }}
        >
          {company.name.charAt(0)}
        </Box>
        <Box>
          <Typography
            variant="body1"
            sx={{
              fontWeight: 700,
              color: theme.palette.text.primary,
              fontSize: '1rem',
              lineHeight: 1.2,
            }}
          >
            {company.name}
          </Typography>
          <Typography
            variant="caption"
            sx={{ color: theme.palette.text.secondary, fontSize: '0.7rem' }}
          >
            Application Pro
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

const NavigationList = ({ isActive, onNavigate }: NavigationListProps) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        pt: 2,
        position: 'relative',
        zIndex: 1,
      }}
    >
      <List disablePadding>
        {FOAM_NAV.map((item) => {
          const active = isActive(item.path);
          return (
            <ListItem key={item.path} disablePadding>
              <ListItemButton
                onClick={() => onNavigate(item.path)}
                sx={{
                  minHeight: 48,
                  width: '100%',
                  px: 3,
                  py: 1.5,
                  borderRadius: 0,
                  transition: 'all 0.2s ease',
                  ...(active
                    ? {
                        color: theme.palette.text.primary,
                        borderBottom: `2px solid ${PRIMARY_COLOR}`,
                      }
                    : {
                        color: theme.palette.text.secondary,
                        borderBottom: '2px solid transparent',
                        '&:hover': {
                          bgcolor: theme.palette.action.hover,
                          color: theme.palette.text.primary,
                        },
                      }),
                }}
              >
                <ListItemText
                  primary={item.label}
                  primaryTypographyProps={{
                    fontWeight: active ? 700 : 500,
                    fontSize: '0.95rem',
                    color: 'inherit',
                    letterSpacing: '-0.01em',
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
    </Box>
  );
};

const ProfileMenu = ({
  anchorEl,
  username,
  roleDisplayName,
  onClose,
  onNavigate,
  onToggleTheme,
  onLogout,
}: ProfileMenuProps) => {
  const theme = useTheme();
  const { mode } = useThemeMode();

  return (
    <Menu
      anchorEl={anchorEl}
      id="account-menu"
      open={Boolean(anchorEl)}
      onClose={onClose}
      onClick={onClose}
      TransitionComponent={Fade}
      PaperProps={{
        elevation: 0,
        sx: {
          overflow: 'visible',
          background: theme.palette.background.paper,
          color: theme.palette.text.primary,
          border: `1px solid ${theme.palette.divider}`,
          boxShadow: mode === 'dark' ? '0 4px 16px rgba(0,0,0,0.2)' : '0 2px 8px rgba(0,0,0,0.06)',
          mt: 1.5,
          minWidth: 260,
          borderRadius: 0,
        },
      }}
      transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
    >
      {/* Profile header */}
      <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <UserAvatar username={username} size={44} />
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 700,
                color: theme.palette.text.primary,
                fontSize: '0.95rem',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {username ?? en.layout.userFallback}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontSize: '0.75rem',
                display: 'block',
              }}
            >
              {roleDisplayName ?? 'User'}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Menu items */}
      <Box sx={{ py: 0.5 }}>
        <MenuItem onClick={() => onNavigate('/admin')} sx={getMenuItemStyles(theme)}>
          <ListItemIcon
            sx={{
              color: theme.palette.text.secondary,
              minWidth: 38,
              transition: 'color 0.15s ease',
            }}
          >
            <AdminPanelSettings fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary="Administration"
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: 'inherit' }}
          />
        </MenuItem>

        <MenuItem onClick={() => onNavigate('/settings')} sx={getMenuItemStyles(theme)}>
          <ListItemIcon
            sx={{
              color: theme.palette.text.secondary,
              minWidth: 38,
              transition: 'color 0.15s ease',
            }}
          >
            <Settings fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={en.layout.settings}
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: 'inherit' }}
          />
        </MenuItem>

        <MenuItem onClick={onToggleTheme} sx={getMenuItemStyles(theme)}>
          <ListItemIcon
            sx={{
              color: theme.palette.text.secondary,
              minWidth: 38,
              transition: 'color 0.15s ease',
            }}
          >
            {mode === 'dark' ? <LightMode fontSize="small" /> : <DarkMode fontSize="small" />}
          </ListItemIcon>
          <ListItemText
            primary={mode === 'dark' ? 'Mode clair' : 'Mode sombre'}
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: 'inherit' }}
          />
        </MenuItem>

        <Divider sx={{ borderColor: theme.palette.divider, opacity: 0.3, my: 0.5, mx: 2 }} />

        <MenuItem onClick={onLogout} sx={getMenuItemStyles(theme, '#e57373')}>
          <ListItemIcon
            sx={{
              color: theme.palette.text.secondary,
              minWidth: 38,
              transition: 'color 0.15s ease',
            }}
          >
            <Logout fontSize="small" />
          </ListItemIcon>
          <ListItemText
            primary={en.layout.logout}
            primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: 500, color: 'inherit' }}
          />
        </MenuItem>
      </Box>
    </Menu>
  );
};

const ToggleButton = ({ isOpen, onClick }: ToggleButtonProps) => {
  const theme = useTheme();

  return (
    <Box
      onClick={onClick}
      sx={{
        display: { xs: 'none', sm: 'flex' },
        position: 'fixed',
        left: isOpen ? `${DRAWER_WIDTH}px` : '0px',
        top: '50%',
        transform: 'translateY(-50%)',
        width: 28,
        height: 56,
        bgcolor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        borderRight: `1px solid ${theme.palette.divider}`,
        borderBottom: `1px solid ${theme.palette.divider}`,
        borderTopRightRadius: '28px',
        borderBottomRightRadius: '28px',
        boxShadow: 'none',
        transition: 'left 0.3s ease, background-color 0.2s ease',
        zIndex: 1201,
        cursor: 'pointer',
        alignItems: 'center',
        justifyContent: 'center',
        '&:hover': {
          bgcolor: theme.palette.action.hover,
          '& .toggle-icon': {
            color: theme.palette.primary.main,
          },
        },
      }}
    >
      {isOpen ? (
        <ChevronLeft
          className="toggle-icon"
          sx={{
            fontSize: '1.5rem',
            color: theme.palette.text.secondary,
            transition: 'color 0.3s ease',
          }}
        />
      ) : (
        <ChevronRight
          className="toggle-icon"
          sx={{
            fontSize: '1.5rem',
            color: theme.palette.text.secondary,
            transition: 'color 0.3s ease',
          }}
        />
      )}
    </Box>
  );
};

const MainLayout = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [desktopOpen, setDesktopOpen] = useState(true);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const user = useCurrentUser();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { mode, toggleTheme } = useThemeMode();

  if (location.pathname === '/login' || FULLSCREEN_PATHS.has(location.pathname)) {
    return <Outlet />;
  }

  // Event handlers
  const handleDrawerToggle = () => setMobileOpen(!mobileOpen);
  const handleDesktopDrawerToggle = () => setDesktopOpen(!desktopOpen);
  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleProfileMenuClose = () => setAnchorEl(null);

  const handleLogout = async () => {
    try {
      await logout.mutateAsync();
      navigate('/login');
    } catch {
      /* logout API error - navigation quand même */
    }
    handleProfileMenuClose();
  };

  const handleNavigation = (path: string) => {
    navigate(path);
    handleProfileMenuClose();
    if (isMobile) setMobileOpen(false);
  };

  const isActive = (path: string) =>
    location.pathname === path || (path === '/admin' && location.pathname.startsWith('/admin'));

  const drawer = (
    <Box
      sx={{
        height: '100%',
        background: theme.palette.background.paper,
        display: 'flex',
        flexDirection: 'column',
        transition: 'all 0.3s ease',
      }}
    >
      <DrawerHeader />
      <ActiveRunSelector />
      <NavigationList isActive={isActive} onNavigate={handleNavigation} />
      <UserProfile
        username={user?.username}
        roleDisplayName={user?.roleDisplayName}
        onProfileMenuOpen={handleProfileMenuOpen}
      />
    </Box>
  );

  const activePageTitle = FOAM_NAV.find((item) => isActive(item.path))?.label ?? company.name;

  return (
    <Box
      sx={{
        display: 'flex',
        minHeight: '100dvh',
        backgroundColor: theme.palette.background.default,
        position: 'relative',
      }}
    >
      <CssBaseline />
      {/* Subtle pattern overlay to match login */}
      {mode === 'dark' && (
        <Box
          aria-hidden
          sx={{
            position: 'fixed',
            inset: 0,
            pointerEvents: 'none',
            opacity: 0.06,
            backgroundImage:
              'repeating-linear-gradient(90deg, rgba(217,217,217,0.35) 0px, rgba(217,217,217,0.35) 1px, transparent 1px, transparent 20px), repeating-linear-gradient(0deg, rgba(217,217,217,0.35) 0px, rgba(217,217,217,0.35) 1px, transparent 1px, transparent 20px)',
          }}
        />
      )}

      {/* Mobile header - only visible on mobile */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          display: { sm: 'none' },
          bgcolor: mode === 'dark' ? 'rgba(46,48,110,0.95)' : 'rgba(255,255,255,0.95)',
          color: theme.palette.text.primary,
          borderBottom: `1px solid ${theme.palette.divider}`,
          boxShadow: mode === 'dark' ? '0 2px 8px rgba(0,0,0,0.15)' : '0 1px 4px rgba(0,0,0,0.05)',
          backdropFilter: 'blur(20px)',
        }}
      >
        <Toolbar sx={{ minHeight: '64px !important' }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{
              mr: 2,
              borderRadius: '12px',
              '&:hover': { bgcolor: 'rgba(217,217,217,0.1)' },
            }}
          >
            <MenuIcon />
          </IconButton>

          <Typography
            variant="h6"
            noWrap
            sx={{
              flexGrow: 1,
              fontWeight: 700,
              fontSize: '1.1rem',
              letterSpacing: '-0.02em',
            }}
          >
            {activePageTitle}
          </Typography>

          <IconButton
            onClick={handleProfileMenuOpen}
            sx={{
              p: 0,
              transition: 'all 0.2s ease',
              '&:hover': { transform: 'scale(1.05)' },
            }}
            aria-controls="account-menu"
            aria-haspopup="true"
          >
            <UserAvatar username={user?.username} size={38} />
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <ProfileMenu
        anchorEl={anchorEl}
        username={user?.username}
        roleDisplayName={user?.roleDisplayName}
        onClose={handleProfileMenuClose}
        onNavigate={handleNavigation}
        onToggleTheme={toggleTheme}
        onLogout={handleLogout}
      />

      {/* Navigation Drawer */}
      <Box
        component="nav"
        sx={{
          width: { sm: desktopOpen ? DRAWER_WIDTH : 0 },
          flexShrink: { sm: 0 },
          transition: 'width 0.3s ease',
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: 'none',
              boxShadow: '4px 0 16px rgba(0,0,0,0.15)',
            },
          }}
        >
          {drawer}
        </Drawer>

        <Drawer
          variant="persistent"
          open={desktopOpen}
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: DRAWER_WIDTH,
              borderRight: 'none',
              boxShadow: '4px 0 16px rgba(0,0,0,0.1)',
              transition: 'transform 0.3s ease',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Floating Toggle Button */}
      <ToggleButton isOpen={desktopOpen} onClick={handleDesktopDrawerToggle} />

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: { sm: desktopOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : '100%' },
          minHeight: '100dvh',
          backgroundColor: 'transparent',
          position: 'relative',
          zIndex: 1,
          transition: 'width 0.3s ease',
        }}
      >
        {/* Spacer only for mobile to account for the AppBar */}
        <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
          <Toolbar sx={{ minHeight: '64px !important' }} />
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 } }}>
          <Container maxWidth={false} disableGutters>
            <Outlet />
          </Container>
        </Box>
      </Box>
    </Box>
  );
};

export default MainLayout;
